import { useRef } from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getAnimation, hexToRgba, buildSrcSet, getImageSizes } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

export default function PartnersSection({ block = {}, primaryColor, secondaryColor, enableAnimations, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const variant = config.variant || 'grid';
    const accent = config.accent_color || primaryColor;
    const logoStyle = config.logo_style || 'natural';
    const logoFit = config.logo_fit || 'contain';
    const logoSz = config.logo_size || 'normal';
    const useGrayscale = config.grayscale !== false;
    const rawItems = content.items || [];
    const scrollRef = useRef(null);

    // Resolve images per item from mediaFolders
    const allItems = rawItems.map(item => {
        let imgUrl = item.image_url || null;
        let imgFile = null;
        if (item.image_folder_id && item.image_file_id && mediaFolders[item.image_folder_id]) {
            const found = mediaFolders[item.image_folder_id].find(f => String(f.id) === String(item.image_file_id));
            if (found) { imgUrl = found.image_url; imgFile = found; }
        }
        return { ...item, image_url: imgUrl, _file: imgFile };
    });

    const partnerColumns = config.columns || 5;
    const partnerColClass = partnerColumns === 3 ? 'grid-cols-2 sm:grid-cols-3'
        : partnerColumns === 4 ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4'
        : partnerColumns === 6 ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6'
        : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5';

    if (allItems.length === 0) return null;

    // Logo container styles
    const logoContainerCls = {
        natural: '',
        circle: 'rounded-full p-4',
        rounded: 'rounded-2xl p-4',
        bordered: 'rounded-xl p-4 border-2',
        glass: 'rounded-xl p-4 backdrop-blur-md',
    }[logoStyle] || '';

    const logoContainerStyle = {
        natural: {},
        circle: { backgroundColor: s.dark ? 'rgba(255,255,255,0.08)' : '#f8fafc' },
        rounded: { backgroundColor: s.dark ? 'rgba(255,255,255,0.08)' : '#f8fafc' },
        bordered: { borderColor: s.dark ? 'rgba(255,255,255,0.15)' : '#e5e7eb', backgroundColor: 'transparent' },
        glass: { backgroundColor: s.dark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.7)', border: '1px solid ' + (s.dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)') },
    }[logoStyle] || {};

    // Size classes based on logo_size
    const sizeMap = {
        small: { container: logoStyle === 'circle' ? 'w-16 h-16' : 'w-20 h-14', img: 'h-8', fallback: 'text-lg', fallbackContainer: logoStyle === 'circle' ? 'w-16 h-16 rounded-full' : 'w-20 h-14 rounded-xl' },
        normal: { container: logoStyle === 'circle' ? 'w-24 h-24' : 'w-28 h-20', img: 'h-12', fallback: 'text-2xl', fallbackContainer: logoStyle === 'circle' ? 'w-24 h-24 rounded-full' : 'w-28 h-20 rounded-xl' },
        large: { container: logoStyle === 'circle' ? 'w-32 h-32' : 'w-36 h-28', img: 'h-16', fallback: 'text-3xl', fallbackContainer: logoStyle === 'circle' ? 'w-32 h-32 rounded-full' : 'w-36 h-28 rounded-xl' },
    };
    const sz = sizeMap[logoSz] || sizeMap.normal;
    const fitCls = logoFit === 'cover' ? 'object-cover w-full h-full' : logoFit === 'fill' ? 'object-fill w-full h-full' : `${sz.img} w-auto max-w-full object-contain`;
    const containerSize = logoStyle === 'natural' ? '' : sz.container;
    const grayscaleCls = useGrayscale ? 'grayscale hover:grayscale-0 transition-all duration-300' : '';

    const LogoImage = ({item}) => (
        item.image_url ? (
            logoStyle === 'natural' ? (
                <div className={`flex items-center justify-center ${grayscaleCls} hover:scale-105 transition-all duration-300`}>
                    <img src={item.image_url} alt={item.name || ''} className={`${sz.img} w-auto max-w-[140px] object-contain`} loading="lazy" decoding="async" srcSet={buildSrcSet(item._file)} sizes={getImageSizes(6)}/>
                </div>
            ) : logoFit === 'contain' ? (
                <div className={`flex items-center justify-center overflow-hidden ${containerSize} ${logoContainerCls} ${grayscaleCls} hover:shadow-md transition-all duration-300`}
                    style={logoContainerStyle}>
                    <img src={item.image_url} alt={item.name || ''} className={`${sz.img} w-auto max-w-full object-contain`} loading="lazy" decoding="async" srcSet={buildSrcSet(item._file)} sizes={getImageSizes(6)}/>
                </div>
            ) : (
                <div className={`relative overflow-hidden ${containerSize} ${logoContainerCls} ${grayscaleCls} hover:shadow-md transition-all duration-300`}
                    style={logoContainerStyle}>
                    <img src={item.image_url} alt={item.name || ''} className={`absolute inset-0 w-full h-full ${logoFit === 'cover' ? 'object-cover' : 'object-fill'}`} loading="lazy" decoding="async" srcSet={buildSrcSet(item._file)} sizes={getImageSizes(6)}/>
                </div>
            )
        ) : (
            <div className={`flex items-center justify-center ${sz.fallbackContainer}`}
                style={{ backgroundColor: hexToRgba(accent, 0.06) }}>
                <span className={`${sz.fallback} font-bold`} style={{ color: accent }}>{item.name?.charAt(0) || '?'}</span>
            </div>
        )
    );

    const sectionCls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;

    // Carousel variant
    if (variant === 'carousel') {
        return (
            <section className={sectionCls} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                    <div className="overflow-hidden">
                        <div ref={scrollRef} className="flex gap-10 items-center animate-[partnerScroll_30s_linear_infinite] hover:[animation-play-state:paused]" style={{ width: 'max-content' }}>
                            {[...allItems, ...allItems].map((item, i) => (
                                <div key={i} className="flex flex-col items-center gap-3 shrink-0">
                                    <LogoImage item={item}/>
                                    {item.name && <span className="text-xs font-medium text-center" style={{ color: s.textSub }}>{item.name}</span>}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color="transparent"  config={config}/>
                <style>{`@keyframes partnerScroll { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }`}</style>
            </section>
        );
    }

    // Grid variant (default)
    return (
        <section className={sectionCls} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                {content.title && <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                <div className={`grid ${partnerColClass} gap-8`}>
                    {allItems.map((item, i) => (
                        <div key={i}
                            className={`flex flex-col items-center text-center gap-3 transition-all duration-500 hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                            style={{ transitionDelay: visible ? `${i * 60}ms` : '0ms' }}>
                            <LogoImage item={item}/>
                            {item.name && <h4 className="text-sm font-semibold" style={{ color: s.text }}>{item.name}</h4>}
                            {item.description && <p className="text-xs" style={{ color: s.textSub }}>{item.description}</p>}
                        </div>
                    ))}
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color="transparent"  config={config}/>
        </section>
    );
}
